import { useCallback } from 'react';
import { vaultApi } from '@/api/vault';
import { useVault } from './VaultContext';
import {
  generateSendSecret,
  deriveSendKey,
  encryptCipherString,
  encryptBuffer,
  base64urlEncode,
  toArrayBuffer,
} from '@/lib/vault-crypto';
import { toast } from 'sonner';

export interface SendOptions {
  /** Дата удаления (ISO string). Обязательно. */
  deletionDate: string;
  /** Дата истечения (ISO string). null = без срока. */
  expirationDate: string | null;
  /** Макс. просмотров. null = без лимита. */
  maxAccessCount: number | null;
  /** Скрыть текст при открытии. */
  hidden: boolean;
}

/**
 * Hook для создания Bitwarden Send.
 * Вся crypto-логика инкапсулирована здесь, не в VaultContext.
 */
export function useSend() {
  const { accessToken, getUserKey, startSessionTimer, expiresAt } = useVault();

  const createSend = useCallback(
    async (text: string, name: string, options: SendOptions): Promise<string> => {
      const userKey = getUserKey();
      if (!userKey || !accessToken) {
        throw new Error('Vault locked');
      }

      // Extend client session (but not beyond server token expiry)
      if (expiresAt) {
        const SESSION_TIMEOUT_MS = 5 * 60 * 1000;
        startSessionTimer(Math.min(Date.now() + SESSION_TIMEOUT_MS, expiresAt));
      }

      // 1. Generate 128-bit random secret
      const sendSecret = generateSendSecret();

      // 2. Derive Send encryption key via full HKDF
      const sendKey = await deriveSendKey(sendSecret);

      // 3. Encrypt text and name with derived Send key
      const encName = await encryptCipherString(name, sendKey);
      const encText = await encryptCipherString(text, sendKey);

      // 4. Encrypt sendSecret (raw bytes) with user key
      //    Using encryptBuffer, NOT encryptCipherString (raw bytes, not UTF-8 string)
      const encSendKey = await encryptBuffer(toArrayBuffer(sendSecret), userKey);

      // 5. Create Send via API
      const response = await vaultApi.createSend(accessToken, {
        type: 0, // Text
        key: encSendKey,
        name: encName,
        text: {
          text: encText,
          hidden: options.hidden,
        },
        deletionDate: options.deletionDate,
        expirationDate: options.expirationDate,
        maxAccessCount: options.maxAccessCount,
        hideEmail: false,
        disabled: false,
      });

      // 6. Build URL: sendUrl (from backend) + base64url(sendSecret)
      const sendUrl = response.sendUrl as string;
      const urlKey = base64urlEncode(toArrayBuffer(sendSecret));

      return `${sendUrl}/${urlKey}`;
    },
    [accessToken, getUserKey, startSessionTimer, expiresAt],
  );

  const createSendSafe = useCallback(
    async (text: string, name: string, options: SendOptions): Promise<string | null> => {
      try {
        return await createSend(text, name, options);
      } catch (error: unknown) {
        if (error instanceof Error) {
          if (error.message === 'Vault locked') {
            toast.error('Сессия vault истекла. Разблокируйте vault.');
          } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
            toast.error('Токен vault истёк. Разблокируйте vault заново.');
          } else {
            toast.error(`Ошибка: ${error.message}`);
          }
        } else {
          toast.error('Неизвестная ошибка при создании Send');
        }
        return null;
      }
    },
    [createSend],
  );

  return { createSend: createSendSafe };
}
